import {
    AlertTriangle,
    Minus,
    Package,
    Plus,
    X,
} from 'lucide-react';

import { formatMoney } from './formatters';
import type { CartItem } from './types';

import {
    Card,
    CardContent,
} from '@/components/ui/card';
import { usePriceVisible } from '@/components/ui/price';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import type { StockError } from '@/pages/MyOrders/types';
import type { UnitOption } from '@/pages/RelatedParties/components/cart-utils';

interface CartItemCardProps {
    item: CartItem;
    supplierName: string;
    supplierId: number | undefined;
    isPending: boolean;
    editingQuantity: number | undefined;
    stockError?: StockError;
    onNavigateToProduct: (supplierId: number | undefined, slug: string) => void;
    onEditQuantity: (slug: string, value: number) => void;
    onCommitQuantity: (item: CartItem) => void;
    onIncrease: (item: CartItem) => void;
    onDecrease: (item: CartItem) => void;
    onRemove: (item: CartItem) => void;
    onChangeUnit?: (item: CartItem, nextUnitSlug: string) => void;
    unitOptions?: UnitOption[];
    canManage?: boolean;
}

export function CartItemCard({
    item,
    supplierName,
    supplierId,
    isPending,
    editingQuantity,
    stockError,
    onNavigateToProduct,
    onEditQuantity,
    onCommitQuantity,
    onIncrease,
    onDecrease,
    onRemove,
    onChangeUnit,
    unitOptions,
    canManage = true,
}: CartItemCardProps) {
    const priceVisible = usePriceVisible();
    const lockedByPerm = !canManage;
    const switchableUnits = unitOptions ?? [];
    const canChangeUnit = !lockedByPerm
        && !!onChangeUnit
        && switchableUnits.length > 1;
    const handleUnitChange = (nextUnitSlug: string) => {
        onChangeUnit?.(item, nextUnitSlug);
    };
    const linePrice = item.total_price || item.original_total_price || '0';
    const unitPrice = item.discounted_price || item.selling_price || '0';
    const hasDiscount = !!item.selling_price
        && !!item.discounted_price
        && Number(item.selling_price) !== Number(item.discounted_price);

    const goToProduct = () => onNavigateToProduct(supplierId, item.product_slug);

    return (
        <Card
            className={cn(
                'border-0 shadow-sm',
                stockError && 'ring-1 ring-destructive/40',
            )}
        >
            <CardContent>
                <div className="flex gap-4">
                    <div
                        role="button"
                        tabIndex={0}
                        onClick={goToProduct}
                        onKeyDown={(e) => e.key === 'Enter' && goToProduct()}
                        className="shrink-0 cursor-pointer rounded-lg"
                    >
                        {item.product_image ? (
                            <img
                                src={item.product_image}
                                alt={item.product_name}
                                className="h-20 w-20 shrink-0 rounded-lg object-cover bg-muted"
                            />
                        ) : (
                            <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-lg bg-muted">
                                <Package className="h-8 w-8 text-muted-foreground/50" />
                            </div>
                        )}
                    </div>

                    <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                            <div>
                                <button
                                    type="button"
                                    className="truncate text-sm font-semibold cursor-pointer hover:underline text-left"
                                    onClick={goToProduct}
                                >
                                    {item.product_name}
                                </button>
                                <p className="mt-0.5 text-xs text-muted-foreground">
                                    {supplierName}
                                </p>
                                {!canChangeUnit && (
                                    <p className="text-xs text-muted-foreground">
                                        Unit:
                                        {' '}
                                        {item.unit_name}
                                    </p>
                                )}
                                {canChangeUnit && (
                                    <div className="mt-1 flex items-center gap-1.5">
                                        <span className="text-xs text-muted-foreground">
                                            Unit:
                                        </span>
                                        <Select
                                            value={item.unit_slug}
                                            onValueChange={handleUnitChange}
                                            disabled={isPending}
                                        >
                                            <SelectTrigger className="h-7 w-auto min-w-[7rem] text-xs">
                                                <SelectValue placeholder={item.unit_name} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {switchableUnits.map((unit) => (
                                                    <SelectItem key={unit.slug} value={unit.slug}>
                                                        {unit.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}
                                {priceVisible && (
                                    <p className="text-xs text-muted-foreground">
                                        Price:
                                        {' '}
                                        Rs.
                                        {' '}
                                        {formatMoney(unitPrice)}
                                        {hasDiscount && (
                                            <span className="ml-2 line-through opacity-70">
                                                Rs.
                                                {' '}
                                                {formatMoney(item.selling_price)}
                                            </span>
                                        )}
                                    </p>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => onRemove(item)}
                                className="shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                                aria-label="Remove item"
                                disabled={isPending || lockedByPerm}
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>

                        <div className="mt-3 flex items-center justify-between">
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    onClick={() => onDecrease(item)}
                                    className={cn(
                                        'flex h-8 w-8 items-center justify-center rounded-md border border-border transition-colors',
                                        item.quantity <= 1 || isPending || lockedByPerm
                                            ? 'cursor-not-allowed opacity-50'
                                            : 'hover:bg-accent',
                                    )}
                                    disabled={item.quantity <= 1 || isPending || lockedByPerm}
                                >
                                    <Minus className="h-3.5 w-3.5" />
                                </button>
                                {isPending ? (
                                    <span className="flex h-8 w-14 items-center justify-center">
                                        <Spinner className="h-3.5 w-3.5" />
                                    </span>
                                ) : (
                                    <input
                                        type="number"
                                        min={1}
                                        value={editingQuantity ?? item.quantity}
                                        disabled={lockedByPerm}
                                        onChange={(e) => {
                                            const val = parseInt(e.target.value, 10);
                                            if (!Number.isNaN(val) && val >= 1) {
                                                onEditQuantity(item.product_slug, val);
                                            }
                                        }}
                                        onBlur={() => onCommitQuantity(item)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                (e.target as HTMLInputElement).blur();
                                            }
                                        }}
                                        className={[
                                            'h-8 w-14 text-center text-sm font-medium',
                                            'border border-border rounded-md bg-background',
                                            '[appearance:textfield]',
                                            '[&::-webkit-outer-spin-button]:appearance-none',
                                            '[&::-webkit-inner-spin-button]:appearance-none',
                                        ].join(' ')}
                                    />
                                )}
                                <button
                                    type="button"
                                    onClick={() => onIncrease(item)}
                                    className={cn(
                                        'flex h-8 w-8 items-center justify-center rounded-md border border-border transition-colors',
                                        isPending || lockedByPerm
                                            ? 'cursor-not-allowed opacity-50'
                                            : 'hover:bg-accent',
                                    )}
                                    disabled={isPending || lockedByPerm}
                                >
                                    <Plus className="h-3.5 w-3.5" />
                                </button>
                            </div>

                            {priceVisible && (
                                <p className="text-sm font-bold">
                                    Rs.
                                    {' '}
                                    {formatMoney(linePrice)}
                                </p>
                            )}
                        </div>

                        {stockError && (
                            <div className="mt-2 flex items-start gap-1.5 text-xs text-destructive">
                                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                <span>{stockError.reason}</span>
                            </div>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
